/*
 *  -----------------------------------------------------------------------
 *    <copyright file="opra-chart.interface.ts" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import { RefObject } from 'react';

export enum OpraChartSeriesType {
    Scatter,
    Line,
    SmoothLine,
}

export enum OpraChartLineStyle {
    Solid,
    Dashed,
    Dotted,
    DashDot,
}

export const HC_LINE_STYLES: string[] = ['Solid', 'Dash', 'Dot', 'DashDot'];

export enum OpraRag {
    None,
    Red,
    Amber,
    Green,
    Purple,
}

export const PURPLE_NAMES: {
    [key: string]: string;
} = {
    LOWER_PURPLE: 'Lower than study average',
    HIGHER_PURPLE: 'Higher than study average',
};

export const HC_DIRECTION_NAMES: string[] = ['Upper', 'Lower'];
export const HC_THRESHOLD_NAMES: string[] = ['None', 'Severe', 'Moderate', 'Normal'];
export const HC_RAG_NAMES: string[] = ['None', 'Red', 'Amber', 'Green', 'Purple'];
export const HC_COLOR_NAMES: string[] = [
    'Grey',
    ...[...HC_RAG_NAMES].splice(1),
    'Purple',
    'Yellow',
    'Light Blue',
    'Dark Blue',
    'Light Grey',
    'Black',
    'White',
];

export const HC_COLORS_STANDARD: string[] = [
    '#616161',
    '#d0021b',
    '#f5a624',
    '#417505',
    '#420080',
    '#e0b700',
    '#027db4',
    '#003366',
    '#7f7f7f',
    '#000000',
    '#ffffff',
    '#de5103',
];

// taken from highcharts themes https://github.com/highcharts/highcharts/blob/master/ts/Extensions/Themes/SandSignika.ts
export const HC_COLORS_SAND_SIGNIKA: string[] = [
    '#f45b5b',
    '#8085e9',
    '#8d4654',
    '#7798BF',
    '#aaeeee',
    '#ff0066',
    '#eeaaee',
    '#55BF3B',
    '#DF5353',
    '#7798BF',
    '#aaeeee',
];

export const HC_COLORS_MUTED: string[] = HC_COLORS_STANDARD.map((colour: string) => `${colour}b3`);
// export const HC_COLORS_VERY_MUTED: string[] = HC_COLORS_STANDARD.map((colour: string) => `${colour}99`);

export enum OpraColours {
    DarkGrey,
    Red,
    Amber,
    Green,
    Purple,
    Yellow,
    LightBlue,
    DarkBlue,
    LightGrey,
    Black,
    White,
    Orange,
}

export enum OpraTrend {
    NoMovement,
    NoTrend,
    Falling,
    Rising,
    Stable,
}

export const HC_TREND_LABELS: string[] = ['No Movement', 'No Trend', 'Falling', 'Rising', 'Stable'];

export enum OpraMarkerShape {
    Circle = 0,
    Square = 1,
    Diamond = 2,
    Triangle = 3,
    UpsideDownTriangle = 4,
}

export enum OpraStackingOptions {
    none,
    values,
    percentages,
}

export const HC_VERTICAL_ALIGN: string[] = ['top', 'middle', 'bottom'];
export enum OpraVerticalAlign {
    top,
    middle,
    bottom,
}

export const HC_STACKING_OPTIONS: (string | undefined)[] = [undefined, 'normal', 'percent'];

// Now using Bankers Rounding to match the back-ends rounding function
export const FormatNumberForChart = (numIn: number | string): string | undefined | null => {
    if (numIn === undefined || numIn === null) return null;
    const x = Number(numIn) * 10 ** 2;
    const r = Math.round(x);
    const br = (x > 0 ? x : -x) % 1 === 0.5 ? (r % 2 === 0 ? r : r - 1) : r;
    return (br / 10 ** 2).toString();
};

export interface IOpraChartMarker {
    shape: OpraMarkerShape;
    isHollow: boolean;
    size?: number;
    enabled?: boolean;
}

export interface IOpraChartXAxis {
    title: string;
    isDateRange: boolean;
}

export interface IOpraChartConfidence {
    yMin: number;
    yMax: number;
}

export interface IOpraChartPlot {
    x: number | Date;
    y: number;
    z?: number;
    confidence?: IOpraChartConfidence;
    score?: number;
    site?: string;
    rag?: OpraRag;
    trend?: OpraTrend;
    pValue?: number;
    drillDownContext?: string | string[];
    selected?: boolean;
    hideTooltip?: boolean;
    direction?: number;
}

export interface IOpraChartSeriesStyle {
    color: string;
    lineStyle?: OpraChartLineStyle;
    marker?: IOpraChartMarker;
    lineWidth?: number;
}

export interface IOpraChartSeries {
    name: string;
    type: OpraChartSeriesType;
    hasConfidences: boolean;
    data: IOpraChartPlot[];
    style?: IOpraChartSeriesStyle;
    colorPerPoint?: boolean;
    showInLegend?: boolean;
    showInTooltip?: boolean;
}

export interface IOpraChartStaticLine {
    name: string | null;
    style: OpraChartLineStyle;
    color: string;
}

export interface IOpraChartHorizontalLine extends IOpraChartStaticLine {
    y: number;
}

export interface IOpraChartVerticalLine extends IOpraChartStaticLine {
    x: number | Date;
}

export interface IOpraChartKey {
    text: string;
    position: OpraVerticalAlign;
}

export interface IOpraChart {
    plotTitle: string;
    xAxis: IOpraChartXAxis;
    yAxisTitle: string;
    series: IOpraChartSeries[];
    horizontalLines?: IOpraChartHorizontalLine[];
    verticalLines?: IOpraChartVerticalLine[];
    showLegend?: boolean;
    secondaryKey?: IOpraChartKey;
    turboModeThreshold?: number;
    getRef?: (ref: RefObject<any> | null) => void;
    minY?: number;
    maxY?: number;
    legendItemWidth?: number;
    legendWidth?: number;
    isCustomLegendWidth: boolean;
    tooltipShowSeriesLabel?: boolean;
    tooltipShowSeriesThreshold?: boolean;
    hidePlotTitle?: boolean;
}

export interface IOpraColumnSeries {
    name: string;
    color?: string;
    values: number[];
    onClick?: any;
    showInLegend?: boolean;
    custom?: any;
}

export interface IOpraLineByCategorySeries {
    name: string;
    values: number[];
    onClick?: any;
    showInLegend?: boolean;
}

export interface IOpraColumnChart {
    plotTitle: string;
    categories: string[];
    yAxisTitle: string;
    series: IOpraColumnSeries[];
    horizontalLines?: IOpraChartHorizontalLine[];
    stacking?: OpraStackingOptions;
    showLegend?: boolean;
    xAxisTitle?: string;
    getRef?: (ref: RefObject<any> | null) => void;
    setLoadingComplete?: Function;
    secondaryKey?: IOpraChartKey;
    formatTooltip?: Function;
    type?: string;
    hidePlotTitle?: boolean;
    displayFullScreenAndPrintTitle?: boolean;
}

export interface IOpraLineByCategoryChart {
    plotTitle: string;
    categories: string[];
    yAxisTitle: string;
    series: IOpraLineByCategorySeries[];
    horizontalLines?: IOpraChartHorizontalLine[];
    stacking?: OpraStackingOptions;
    showLegend?: boolean;
    xAxisTitle?: string;
    getRef?: (ref: RefObject<any> | null) => void;
    setLoadingComplete?: Function;
    secondaryKey?: IOpraChartKey;
    formatTooltip?: Function;
    hidePlotTitle?: boolean;
}

export interface IOpraHeatMapSeries {
    name: string;
    data: number[];
}

export interface IOpraHeatMapAxis {
    name: string;
    categories: string[];
}

export interface IOpraHeatMap {
    plotTitle: string;
    xAxis: IOpraHeatMapAxis;
    yAxis: IOpraHeatMapAxis;
    series: IOpraHeatMapSeries;
    getRef?: (ref: RefObject<any> | null) => void;
    secondaryKey?: IOpraChartKey;
    showLegend?: boolean;
    hidePlotTitle?: boolean;
}

export interface IOpraGaugePlot {
    name: string;
    value: number;
    color: string;
}

export interface IOpraGaugeChart {
    plotTitle: string;
    plots: IOpraGaugePlot[];
    getRef?: (ref: RefObject<any> | null) => void;
    secondaryKey?: IOpraChartKey;
    showLegend?: boolean;
    hidePlotTitle?: boolean;
}
